import {
  type CalendarView,
  GROUP_BY_OPTIONS,
  type GroupByOption,
  parseCalendarView,
} from "@dream/config/constants";
import { parseClientHandleFromPath } from "@dream/domain";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { api, unwrap } from "./api/client.ts";
import { CookieBanner, CookieConsentProvider } from "./components/cookie-consent.tsx";
import { AdminShell } from "./layouts/admin-shell.tsx";
import { CalendarPage } from "./pages/calendar-page.tsx";
import { ClientViewPage } from "./pages/client-view-page.tsx";
import { ClientsPage } from "./pages/clients-page.tsx";
import { LoginPage } from "./pages/login-page.tsx";
import { MoonPage } from "./pages/moon-page.tsx";
import { NotFoundPage } from "./pages/not-found-page.tsx";
import { PortalPage } from "./pages/portal-page.tsx";
import { PrivacyPage } from "./pages/privacy-page.tsx";
import { ProjectsPage } from "./pages/projects-page.tsx";
import { PublisherPage } from "./pages/publisher-page.tsx";
import {
  parseSettingsSection,
  SettingsPage,
  type SettingsSection,
} from "./pages/settings-page.tsx";
import { readStoredPreferences } from "./preferences/preferences.ts";

/**
 * Rotas em código, sem plugin de geração.
 *
 * O `@tanstack/router-plugin` acopla a versão do plugin à do router e adiciona
 * um arquivo gerado ao build. Com sete rotas, a definição explícita é menor e
 * não tem esse acoplamento.
 *
 * Os filtros do calendário vivem em `search params` validados: o briefing pede
 * "filtros persistidos na URL", e assim um link compartilhado reproduz
 * exatamente a mesma visão.
 */

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: RootNotFound,
});

/**
 * Se o router não casar `/@{$handle}` (caractere `@`, encoding, proxy), a
 * URL do browser ainda é `/@javalibarber` — abre a visão do cliente em vez
 * do 404 genérico que parecia "o sistema não existe".
 */
function RootNotFound() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  if (parseClientHandleFromPath(pathname) !== null) {
    return <ClientViewPage />;
  }
  return <NotFoundPage />;
}

function RootLayout() {
  return (
    <CookieConsentProvider>
      <div className="flex h-dvh flex-col overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </div>
        <CookieBanner />
      </div>
    </CookieConsentProvider>
  );
}

/** Sessão atual. `null` quando não autenticado — não é erro. */
async function fetchSession(): Promise<{
  userId: string;
  email: string;
  organizationId: string;
} | null> {
  try {
    const response = await api.api.me.get();
    if (response.error !== null && response.error !== undefined) return null;
    return response.data as {
      userId: string;
      email: string;
      organizationId: string;
    };
  } catch {
    return null;
  }
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { redirect?: string; error?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  beforeLoad: async ({ search }) => {
    // Já autenticado: não faz sentido mostrar o formulário de login.
    const session = await fetchSession();
    if (session !== null) {
      throw redirect({
        to: search.redirect ?? "/calendario",
        search: calendarSearch(),
      });
    }
  },
  component: LoginPage,
});

/**
 * Layout administrativo.
 *
 * `beforeLoad` bloqueia acesso não autenticado. Isso é conveniência de
 * navegação, não segurança: a autorização real está na API, que rejeita
 * qualquer requisição sem sessão de admin.
 */
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "admin",
  beforeLoad: async ({ location }) => {
    const session = await fetchSession();
    if (session === null) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    return { session };
  },
  component: AdminShell,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacidade",
  component: PrivacyPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/calendario", search: calendarSearch() });
  },
});

export const CALENDAR_SOURCES = ["todas", "entregas", "agenda"] as const;
export type CalendarSource = (typeof CALENDAR_SOURCES)[number];

export interface CalendarSearch {
  view: CalendarView | undefined;
  date: string | undefined;
  groupBy: GroupByOption | undefined;
  clients: string | undefined;
  projects: string | undefined;
  types: string | undefined;
  assignees: string | undefined;
  statuses: string | undefined;
  q: string | undefined;
  colorBy: "type" | "client" | undefined;
  entrega: string | undefined;
  /** Abre o diálogo de nova entrega ao chegar no calendário. */
  nova: string | undefined;
  /** Recorte do overlay: entregas Dream, Agenda, ou os dois. */
  fonte: CalendarSource | undefined;
}

/** Valida os search params, para uma URL manipulada não quebrar a página. */
export function validateCalendarSearch(search: Record<string, unknown>): CalendarSearch {
  const view = parseCalendarView(search.view);

  const groupBy = GROUP_BY_OPTIONS.includes(search.groupBy as GroupByOption)
    ? (search.groupBy as GroupByOption)
    : "none";

  const asString = (value: unknown): string | undefined =>
    typeof value === "string" && value.trim() !== "" ? value : undefined;

  // Uma data inválida na URL volta para hoje, em vez de gerar Invalid Date.
  const rawDate = asString(search.date);
  const date =
    rawDate !== undefined && !Number.isNaN(new Date(rawDate).getTime()) ? rawDate : undefined;

  const fonte = CALENDAR_SOURCES.includes(search.fonte as CalendarSource)
    ? (search.fonte as CalendarSource)
    : "todas";

  return {
    view: view === "mes" ? undefined : view,
    date,
    groupBy: groupBy === "none" ? undefined : groupBy,
    clients: asString(search.clients),
    projects: asString(search.projects),
    types: asString(search.types),
    assignees: asString(search.assignees),
    statuses: asString(search.statuses),
    q: asString(search.q),
    colorBy: search.colorBy === "client" ? "client" : undefined,
    entrega: asString(search.entrega),
    nova: asString(search.nova),
    fonte: fonte === "todas" ? undefined : fonte,
  };
}

/**
 * Search params completos para o calendário.
 *
 * O TanStack Router exige que um link para outra rota forneça o objeto de
 * search inteiro, porque é o tipo validado que define o contrato. Este helper
 * preenche os padrões e aceita apenas o que o chamador quer sobrescrever, para
 * que nenhum `<Link>` precise repetir os oito campos.
 */
export function calendarSearch(overrides: Partial<CalendarSearch> = {}): CalendarSearch {
  const prefs = typeof localStorage === "undefined" ? null : readStoredPreferences();
  const view = parseCalendarView(overrides.view ?? prefs?.defaultView);
  const groupBy = overrides.groupBy ?? "none";
  const colorBy = overrides.colorBy ?? prefs?.colorBy ?? "type";
  const fonte = overrides.fonte ?? "todas";
  return {
    view: view === "mes" ? undefined : view,
    date: overrides.date,
    groupBy: groupBy === "none" ? undefined : groupBy,
    clients: overrides.clients,
    projects: overrides.projects,
    types: overrides.types,
    assignees: overrides.assignees,
    statuses: overrides.statuses,
    q: overrides.q,
    colorBy: colorBy === "type" ? undefined : colorBy,
    entrega: overrides.entrega,
    nova: overrides.nova,
    fonte: fonte === "todas" ? undefined : fonte,
  };
}

const calendarRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/calendario",
  validateSearch: validateCalendarSearch,
  component: CalendarPage,
});

const clientsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/clientes",
  component: ClientsPage,
});

function validateMoonSearch(search: Record<string, unknown>): { apresentacao?: string } {
  const raw = search.apresentacao;
  if (typeof raw === "string" && raw.trim() !== "") return { apresentacao: raw.trim() };
  return {};
}

const moonRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/moon",
  validateSearch: validateMoonSearch,
  component: MoonPage,
});

const hermesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/hermes",
  validateSearch: validateMoonSearch,
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/moon", search });
  },
  component: MoonPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/projetos",
  component: ProjectsPage,
});

const shareLinksRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/compartilhamento",
  beforeLoad: () => {
    throw redirect({ to: "/clientes" });
  },
  component: ClientsPage,
});

function validateSettingsSearch(search: Record<string, unknown>): { secao?: SettingsSection } {
  const secao = parseSettingsSection(search.secao);
  return secao === "geral" ? {} : { secao };
}

const publisherRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/publicacoes",
  component: PublisherPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/configuracoes",
  validateSearch: validateSettingsSearch,
  component: function SettingsRoute() {
    const search = settingsRoute.useSearch();
    return <SettingsPage section={search.secao ?? "geral"} />;
  },
});

/**
 * Portal do cliente.
 *
 * Formato exigido pelo briefing: `/c/{slug}?token={token}`. Fora do layout
 * administrativo de propósito — o portal não tem nenhuma ferramenta de
 * administração, nem no DOM.
 */
const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/c/$slug",
  validateSearch: (search: Record<string, unknown>): CalendarSearch & { token: string } => ({
    ...validateCalendarSearch(search),
    token: typeof search.token === "string" ? search.token : "",
  }),
  component: PortalPage,
});

/**
 * Visão principal do cliente: `/@javalibarber`.
 *
 * Fora do shell administrativo. A senha de 4 dígitos não vai na URL.
 */
const clientViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/@{$handle}",
  validateSearch: validateCalendarSearch,
  component: ClientViewPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  privacyRoute,
  portalRoute,
  clientViewRoute,
  adminRoute.addChildren([
    calendarRoute,
    clientsRoute,
    moonRoute,
    hermesRoute,
    projectsRoute,
    publisherRoute,
    shareLinksRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient: undefined as unknown as QueryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 10_000,
  pathParamsAllowedCharacters: ["@"],
  // O portal recebe o token pela query string; mantê-lo fora do histórico
  // reduz a chance de o token vazar por navegação acidental.
  defaultStaleTime: 10_000,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export {
  calendarRoute,
  clientsRoute,
  clientViewRoute,
  fetchSession,
  hermesRoute,
  loginRoute,
  moonRoute,
  portalRoute,
  projectsRoute,
  shareLinksRoute,
  unwrap,
};
